// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import AbstractAudioComponent from 'core/entities/components/AbstractAudioComponent';

const DEFAULT_ENGINE = 'standard';

/**
 * The Speech Component.
 * Adds text-to-speech functionality to an entity.
 */
class SpeechComponent extends AbstractAudioComponent {
  constructor() {
    super();
    this.type = 'SpeechComponent';

    this._voice = null;
    this._voiceEngine = DEFAULT_ENGINE;
    this._spatial = false;
    /**
     * Current speeches in the entity. Add a speech using {@link SpeechComponent#addSpeech}
     */
    this.speeches = [];
  }

  /**
   * Add a speech to the component
   *
   * @param {Speech} speech
   */
  addSpeech(speech) {
    if (!this.speeches.includes(speech)) {
      if(this._spatial){
        speech.connectTo( [this._inNode, this._outWetNode] );
      } else{
        speech.connectTo( [this._inNode2d] );
      }

      speech.updateConfig({
        entity: this.entity,
        voice: this._voice,
        voiceEngine: this._voiceEngine
      });

      this.speeches.push(speech);
    }
  }

  /**
   * Remove a speech from the component.
   *
   * @param {Speech} speech
   */
  removeSpeech(speech) {
    const index = this.speeches.indexOf(speech);
    if (index > -1) {
      speech.stop();
      this.speeches.splice(index, 1);
      speech.connectTo();
    }
  }

  /**
   * Get a component's speech by id.
   *
   * @param {string} id
   */
  getSpeechById(id) {
    return this.speeches.find(speech => speech.id === id);
  }

  /**
   * Set spatial field of speechComponent
   * @param {boolean} spatial
   */
  setSpatial(spatial) {
    this._spatial = spatial;
    if(this._spatial){
      this.speeches.forEach(speech => {
        speech.connectTo( [this._inNode, this._outWetNode] );
      });
    } else{
      this.speeches.forEach(speech => {
        speech.connectTo( [this._inNode2d] );
      });
    }
  }

  /**
   * Get spatial field of speechComponent
   * @return {boolean}
   */
  getSpatial(){
    return this._spatial;
  }

  /**
   * Sets the voice for all speeches on the Speech Component.
   *
   * @param {string} voice Voice Id of the voice (e.g. "Amy")
   * @returns {Promise} Promise resolving with the new voice
   */
  setVoice(voice) {
    if(this._voice !== voice) {
      // update the voiceId for all speeches in this component
      this._voice = voice;
      this.speeches.forEach(speech => speech.updateConfig({voice: this._voice, voiceEngine: this._voiceEngine}));
    }
    return Promise.resolve(this._voice);
  }

  /**
   * Get the current Voice Id
   * @returns {string} Voice Id of the voice (e.g. "Amy")
   */
  getVoice() {
    return this._voice;
  }

  setEngine(voiceEngine) {
    if(this._voiceEngine !== voiceEngine) {
      // update the voiceEngine for all speeches in this component
      this._voiceEngine = voiceEngine;
      this.speeches.forEach(speech => speech.updateConfig({voiceEngine: this._voiceEngine, voice: this._voice}));
    }
    return Promise.resolve(this._voiceEngine);
  }

  getEngine() {
    return this._voiceEngine;
  }

  setVoiceAndEngine(voice, voiceEngine) {
    const obj = {};
    if (voice && this._voice !== voice) {
      this._voice = voice;
      obj.voice = voice;
    }
    if (voiceEngine && this._voiceEngine !== voiceEngine){
      this._voiceEngine = voiceEngine.toLowerCase();
      obj.voiceEngine = this._voiceEngine;
    }
    this.speeches.forEach(speech => speech.updateConfig(obj));

    return Promise.resolve();
  }

  /**
   * Updates the component values according to a config.
   *
   * @typedef {Object} config
   * @property {number} config.volume A number between 0 and 1 representing the playback volume.
   * @property {string} config.voice  The name of the polly voice to use for playback.
   * @property {boolean} config.spatial  Indicate if the component is a spatial speech component
   */
  updateConfig(config) {
    if (config.volume) {
      this.setVolume(config.volume);
    }
    if (config.voice) {
      this.setVoice(config.voice);
    }
    if (config.voiceEngine) {
      this.setEngine(config.voiceEngine);
    }
    if (config.spatial !== undefined && config.spatial !== this._spatial){
      this.setSpatial(config.spatial);
    }
  }


  start() {
    // handled by process
  }

  /**
   * pause all speeches if they are playing
   */
  pause() {
    for (const speech of this.speeches) {
      speech.pause();
    }
  }

  /**
   * resume all speeches if they were playing
   */
  resume() {
    for (const speech of this.speeches) {
      speech.resume();
    }
  }

  /**
   * Stop all speeches
   */
  stop() {
    for (const speech of this.speeches) {
      speech.stop();
    }
  }

  /**
   * Processes each speech attached to the component.
   * Called once per frame while scene is playing.
   * @param {Object} settings settings for pannerNode
   * @param {Matrix4} mvMat  relative transform matrix for pannerNode
   */
  process(settings,mvMat) {

    if(this._spatial){
      this.updatePannerNode(settings,mvMat);
    }

    for (const speech of this.speeches) {
      speech.process();
    }

  }

  // Available Polly voice. This is used to populate the ChangeSpeechVoiceAction voice options
  // TODO: SUMERIAN-1737 - get this list from the Polly API
  static get VOICES() {
    return [
      "Aditi",
      "Amy",
      "Astrid",
      "Bianca",
      "Brian",
      "Camila",
      "Carla",
      "Carmen",
      "Celine",
      "Chantal",
      "Conchita",
      "Cristiano",
      "Dora",
      "Emma",
      "Enrique",
      "Ewa",
      "Filiz",
      "Geraint",
      "Giorgio",
      "Gwyneth",
      "Hans",
      "Ines",
      "Ivy",
      "Jacek",
      "Jan",
      "Joanna",
      "Joey",
      "Justin",
      "Karl",
      "Kendra",
      "Kimberly",
      "Lea",
      "Liv",
      "Lotte",
      "Lucia",
      "Lupe",
      "Mads",
      "Maja",
      "Marlene",
      "Mathieu",
      "Matthew",
      "Maxim",
      "Mia",
      "Miguel",
      "Mizuki",
      "Naja",
      "Nicole",
      "Penelope",
      "Raveena",
      "Ricardo",
      "Ruben",
      "Russell",
      "Salli",
      "Seoyeon",
      "Takumi",
      "Tatyana",
      "Vicki",
      "Vitoria",
      "Zeina",
      "Zhiyu",
    ];
  }

  static get VOICE_ENGINES() {
    return [
      "Neural",
      "Standard",
    ];
  }
}

export default SpeechComponent;
